import { useCheckProtectedResources } from '@/components/InvoiceReportDetailDrawer/hooks/useCheckProtectedResources';
import {
  AdminInvoiceReportExportStatus,
  SearchAdminInvoiceReportsCsvEncode,
  useExportAdminInvoiceReportsMutation,
  type AdminInvoiceReportExportResult,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportExport.service';
import { ForbiddenCodeEnum } from '@/context/services/type';
import type { FetchAdminInvoiceReportsArgs } from '@/features/admin_invoice_reports/types';
import { useDownload } from '@/hooks/useDownload';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { DATE_FORMAT_HYPHEN, getCurrentDate } from 'date-util';
import { useCallback } from 'react';

const GENERIC_ERROR_KEY = 'admin_invoice_reports.export.error_message';
const ASYNC_STARTED_KEY = 'admin_invoice_reports.export.async_started';

async function handleExportResult(
  result: AdminInvoiceReportExportResult,
  deps: {
    download: ReturnType<typeof useDownload>;
    notify: ReturnType<typeof useToastAPI>;
    t: (key: string) => string;
    fileName: string;
    handleCheckPermission: ReturnType<
      typeof useCheckProtectedResources
    >['handleCheckPermission'];
    refetch: () => Promise<unknown>;
  }
): Promise<void> {
  const { download, notify, t, fileName, handleCheckPermission, refetch } =
    deps;

  switch (result.status) {
    case AdminInvoiceReportExportStatus.SUCCESS:
      if (result.message) {
        notify.success({ title: toHtmlNode(result.message), duration: 3 });
        return;
      }
      download({ value: result.blob, fileName });
      return;

    case AdminInvoiceReportExportStatus.REDIRECT_TO_TEMPFILES:
      notify.success({
        title: toHtmlNode(t(ASYNC_STARTED_KEY)),
        duration: 3,
      });
      window.location.href = result.redirectUrl;
      return;

    case AdminInvoiceReportExportStatus.UNAUTHORIZED:
    case AdminInvoiceReportExportStatus.NOT_FOUND:
      location.reload();
      return;

    case AdminInvoiceReportExportStatus.FORBIDDEN: {
      // Mimic mode: toast (API message or generic)
      if (result.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
        notify.error({
          title: toHtmlNode(result.message ?? t(GENERIC_ERROR_KEY)),
          duration: 3,
        });
        return;
      }

      if (result.message) {
        notify.error({
          title: toHtmlNode(result.message),
          duration: 3,
        });
      }
      // 403 empty body → verify list access before surfacing error or reloading
      await handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: () => {
          notify.error({
            title: toHtmlNode(t(GENERIC_ERROR_KEY)),
            duration: 3,
          });
        },
        onCheckFailure: () => {
          location.reload();
        },
      });
      return;
    }

    case AdminInvoiceReportExportStatus.ERROR:
      // Matches old toastIsGeneric: true — always generic toast
      notify.error({
        title: toHtmlNode(t(GENERIC_ERROR_KEY)),
        duration: 3,
      });
      return;
  }
}

export const useExportAdminInvoiceReportsQuery = (
  query: FetchAdminInvoiceReportsArgs,
  { refetch }: { refetch: () => Promise<unknown> }
) => {
  const { t } = useTranslation();
  const download = useDownload();
  const notify = useToastAPI();
  const { mutateAsync, isPending } = useExportAdminInvoiceReportsMutation();
  const { handleCheckPermission } = useCheckProtectedResources();

  const handleExport = useCallback(
    (csv_encode: SearchAdminInvoiceReportsCsvEncode) => async () => {
      const fileName = `${t(
        'admin_invoice_reports.export.filename'
      )}_${getCurrentDate().format(DATE_FORMAT_HYPHEN)}.csv`;
      try {
        const result = await mutateAsync({ ...query, csv_encode });
        await handleExportResult(result, {
          download,
          notify,
          t,
          fileName,
          handleCheckPermission,
          refetch,
        });
      } catch (error: unknown) {
        // Thrown before mapper runs (e.g. network / client from openapi) — not AdminInvoiceReportExportResult
        // eslint-disable-next-line no-console
        console.error(
          'useExportAdminInvoiceReportsQuery: export request failed',
          error
        );
        const title =
          error instanceof Error && error.message
            ? error.message
            : t(GENERIC_ERROR_KEY);
        notify.error({
          title: toHtmlNode(title),
          duration: 3,
        });
      }
    },
    [mutateAsync, query, download, notify, t, handleCheckPermission, refetch]
  );

  return { isLoading: isPending, handleExport };
};
